"use client"
import {RefObject} from "react"
import {Button} from "@/components/ui/button"
import {Separator} from "@/components/ui/separator"
import {step1Schema} from "@/schemas/auth"
import {Form} from "@/components/form/Form";
import {TextField} from "@/components/form/TextField";
import {CheckboxField} from "@/components/form/CheckboxField";
import {PhoneField} from "@/components/form/PhoneField/PhoneField";
import {FormRef, OnSubmit, ZodObjectType} from "@/components/form/utils";
import {Account} from "@/app/account-request/@steps/step1/components/step-1-account-wrapper";

type SocialLoginProvider = "google" | "facebook"

interface Step1Props<S extends ZodObjectType> {
    loading?: boolean;
    handleEmailSignUp: OnSubmit<S>
    handleSocialSignUp: (provider: SocialLoginProvider) => Promise<void>;
    account?: Account
    ref: RefObject<FormRef<S,OnSubmit<S>> | undefined>
}


export function Step1Account({
                                 loading,
                                 handleEmailSignUp,
                                 handleSocialSignUp,
                                 account,
                                 ref,
                             }: Step1Props<typeof step1Schema>) {
    const defaultValues = {
        firstName: account?.first_name ?? "",
        lastName: account?.last_name ?? "",
        email: account?.email ?? "",
        cellphoneCountryCode: account?.cellphone_country_code ?? "",
        cellphone: account?.cellphone ?? "",
    }

    return (
        <div className="space-y-6">
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Create Your Account</h2>
                <p className="text-muted-foreground">Get started with your new account</p>
            </div>
            {/* Social Login Options */}
            <div className="space-y-3">
                <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleSocialSignUp("google")}
                    disabled={loading}
                >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    Continue with Google
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleSocialSignUp("facebook")}
                    disabled={loading}
                >
                    <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                        <path
                            d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Continue with Facebook
                </Button>
            </div>
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full"/>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                </div>
            </div>
            <Form
                ref={ref}
                defaultValues={defaultValues}
                onSubmit={handleEmailSignUp}
                schema={step1Schema}
                className="space-y-4"
                submitText={account ? 'Save' : 'Create Account'}>
                <div className="grid grid-cols-2 gap-4">
                    <TextField name={'firstName'} label={'First Name'}/>
                    <TextField name={'lastName'} label={'Last Name'}/>
                    <TextField
                        label={"Email Address"}
                        type="email"
                        name="email"
                    />
                    <PhoneField phoneCountryCodeName={'cellphoneCountryCode'} phoneName={'cellphone'}
                                label={'Cellphone Number'}/>
                </div>
                <div className="border rounded-lg p-4 h-32 overflow-y-auto">
                    <h3 className="font-semibold mb-2">Terms and Conditions</h3>
                    <div className="text-sm text-muted-foreground">
                        <p>1. Acceptance of Terms</p>
                        <p className="mb-2">By accessing and using this service, you accept and agree to be bound by the
                            terms and
                            provision of this agreement.</p>
                        <p>2. User Registration</p>
                        <p className="mb-2">You agree to provide accurate, current, and complete information during the
                            registration
                            process and to update such information to keep it accurate, current, and complete.</p>
                        <p>3. Privacy Policy</p>
                        <p className="mb-2">Your privacy is important to us. Please review our Privacy Policy to
                            understand how we
                            collect, use, and protect your personal information.</p>
                        <p>4. User Conduct</p>
                        <p className="mb-2">You agree to use the service in compliance with all applicable laws and
                            regulations and
                            in accordance with these terms and conditions.</p>
                    </div>
                </div>
                <CheckboxField name={'acceptTerms'} label={<>
                    I agree to the{" "}
                    <a href="#" className="text-primary hover:underline">
                        Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a href="#" className="text-primary hover:underline">
                        Privacy Policy
                    </a>
                </>}/>
            </Form>
        </div>
    )
}

